import {
	CreateDrawerFormProps,
	DrawerBody,
	DrawerFooter,
	DrawerHeader,
	DrawerHeading,
	FormDrawer,
	FormDrawerContent,
	FormDrawerContentProps,
	FormFetchDrawer,
	PagedMultiSelectField,
	SubmitButton,
	TextField,
	UpdateDrawerFormProps,
} from "@dgs/core";
import { ActivityOptionDto, ProjectShowDto } from "apiGen";
import { Form, Formik } from "formik";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { activitySelectFetcher } from "~root/activities/ActivitySelectField";
import { activityKeys } from "~shared/api/activities/activityKeys";
import { projectKeys } from "~shared/api/projects/projectKeys";
import { webserviceApi } from "~shared/api/webserviceApi";

export interface IProjectFormState {
	name: ProjectShowDto["name"];
	description: ProjectShowDto["description"];
	activities: ActivityOptionDto[];
}

const useValidate = () => {
	const { t } = useTranslation();

	return (values: IProjectFormState) => {
		const errors: { [K in keyof IProjectFormState]?: any } = {};

		if (!values.name) {
			errors.name = t("Field is required");
		}
		return errors;
	};
};

const ProjectDrawerContent: FC<FormDrawerContentProps> = (props) => {
	const { t } = useTranslation();

	return (
		<FormDrawerContent {...props}>
			<TextField name="name" label={t("Name")} required />
			<TextField name="description" label={t("Description")} />
			<PagedMultiSelectField
				name="activities"
				label={t("Activities")}
				fetcher={activitySelectFetcher}
				queryKey={[...activityKeys.options()]}
				renderCreateForm={(selectNew, onClose) => {
					return (
						<Formik
							initialValues={{ name: "" }}
							onSubmit={(values) => {
								selectNew({
									id: -1,
									name: values.name,
								});
								onClose();
							}}
						>
							<Form>
								<DrawerHeader onClose={onClose}>
									<DrawerHeading>{t("Create Activity")}</DrawerHeading>
								</DrawerHeader>
								<DrawerBody>
									<TextField name="name" label={t("Name")} />
								</DrawerBody>
								<DrawerFooter>
									<SubmitButton>{t("Submit")}</SubmitButton>
								</DrawerFooter>
							</Form>
						</Formik>
					);
				}}
			/>
		</FormDrawerContent>
	);
};

const initialValues: IProjectFormState = {
	name: "",
	description: "",
	activities: [],
};

export const ProjectCreateDrawer: FC<CreateDrawerFormProps<IProjectFormState>> = (drawerProps) => {
	const { t } = useTranslation();
	const validate = useValidate();

	return (
		<FormDrawer
			{...drawerProps}
			initialValues={initialValues}
			validate={validate}
			title={t("Add project")}
			renderForm={(props) => <ProjectDrawerContent {...props} />}
		/>
	);
};

export const ProjectUpdateDrawer: FC<UpdateDrawerFormProps<IProjectFormState>> = (drawerProps) => {
	const { t } = useTranslation();
	const validate = useValidate();

	return (
		<FormFetchDrawer
			{...drawerProps}
			title={t("Edit project")}
			validate={validate}
			queryFn={() => webserviceApi.showProject(+drawerProps.id).then((x) => x.data.data)}
			queryKey={projectKeys.detail(drawerProps.id)}
			renderSuccess={(props) => <ProjectDrawerContent {...props} />}
		/>
	);
};
